import React from "react";
import { Link } from "react-router-dom";
import { initials, teamColor } from "../metrics";

export function Card({ title, subtitle, actions, children, className = "", padded = true, flush = false }) {
  return (
    <section className={`bd-card ${flush ? "bd-card--flush" : ""} ${className}`}>
      {(title || actions) && (
        <header className="bd-card__header">
          <div>
            {title && <h3 className="bd-card__title">{title}</h3>}
            {subtitle && <p className="bd-card__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="bd-card__actions">{actions}</div>}
        </header>
      )}
      <div className={`bd-card__body ${padded ? "" : "bd-card__body--unpadded"}`}>{children}</div>
    </section>
  );
}

export function Pill({ tone = "neutral", children, color, className = "", title }) {
  const style = color ? { "--pill-color": color } : undefined;
  return (
    <span className={`bd-pill bd-pill--${tone} ${color ? "bd-pill--colored" : ""} ${className}`} style={style} title={title}>
      {children}
    </span>
  );
}

// Initials fall back automatically if the image is missing or fails to load.
export function Avatar({ name, team, size = "md", src }) {
  const color = teamColor(team);
  return (
    <span className={`bd-avatar bd-avatar--${size} ${src ? "bd-avatar--has-image" : ""}`} style={{ "--avatar-color": color }} aria-hidden="true">
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement.classList.remove("bd-avatar--has-image");
          }}
        />
      ) : null}
      <span>{initials(name)}</span>
    </span>
  );
}

export function Skeleton({ height = 16, width = "100%", className = "", radius = 8 }) {
  return <span className={`bd-skeleton ${className}`} style={{ height, width, borderRadius: radius }} />;
}

export function SkeletonRows({ rows = 5 }) {
  return (
    <div className="bd-skeleton-rows">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={38} />
      ))}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", message, icon = "◦", action }) {
  return (
    <div className="bd-empty">
      <div className="bd-empty__icon">{icon}</div>
      <div className="bd-empty__title">{title}</div>
      {message && <div className="bd-empty__message">{message}</div>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="bd-empty bd-empty--error">
      <div className="bd-empty__icon">!</div>
      <div className="bd-empty__title">Couldn't load this view</div>
      {message && <div className="bd-empty__message">{message}</div>}
      {onRetry && (
        <button type="button" className="bd-btn bd-btn--secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function ProgressBar({ value = 0, max = 1, tone = "info", label }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="bd-progress" title={label}>
      <div className={`bd-progress__bar bd-progress__bar--${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SectionTitle({ children, meta }) {
  return (
    <div className="bd-section-title">
      <h2>{children}</h2>
      {meta && <span className="bd-section-title__meta">{meta}</span>}
    </div>
  );
}

export function TeamDot({ team, withName = false, short = false }) {
  return (
    <span className="bd-team-dot">
      <i style={{ background: teamColor(team) }} />
      {withName && <span>{team || "Unassigned"}</span>}
    </span>
  );
}

export function LinkButton({ to, children, className = "" }) {
  return (
    <Link to={to} className={`bd-btn bd-btn--ghost ${className}`}>
      {children}
    </Link>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="bd-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          className={`bd-tabs__tab ${active === t.id ? "is-active" : ""}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count !== undefined && <span className="bd-tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// Small "i" marker with a CSS tooltip (shown on hover and keyboard focus).
export function InfoTip({ text, align = "center" }) {
  if (!text) return null;
  return (
    <span className={`bd-info-tip bd-info-tip--${align}`} tabIndex={0} role="img" aria-label={text} data-tip={text}>
      i
    </span>
  );
}
